import React, { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Menu as PrimeMenu } from "primereact/menu";
import { Button } from "primereact/button";
import { useSelector, useDispatch } from "react-redux";
import { AppDispatch, RootState } from "../../redux/store";
import { getCurrentUser, logout } from "../../redux/slices/authSlice";
import { logMessage } from "../../utils/logMessage";
import { Avatar } from "primereact/avatar";
import { Skeleton } from "primereact/skeleton";
import { fetchAllBalances } from "../../redux/slices/accountSlice";
import { Toast } from "primereact/toast";
import { NotificationButton } from "../features/NotificationButton";
import { customerService } from "../../services/customerService";
import { Dialog } from "primereact/dialog";
import { formatAmount } from "../../utils/functions";

interface NavBarProps {
  isExpanded: boolean;
  setIsExpanded: React.Dispatch<React.SetStateAction<boolean>>;
}

const NavBar: React.FC<NavBarProps> = ({ isExpanded, setIsExpanded }) => {
  const location = useLocation();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const toastRef = useRef<Toast>(null);
  const profileMenuRef = useRef<HTMLDivElement>(null);

   const { currentUser, loading, customerMode } = useSelector((state: RootState) => state.auth);
   const { balances, isLoading } = useSelector((state: RootState) => state.account);

  const [showBalance, setShowBalance] = useState(false);
  const [requestingProduction, setRequestingProduction] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [isProfileMenuOpen, setIsProfileMenuOpen] = useState(false);
  const [showSandboxBanner, setShowSandboxBanner] = useState(true);

  const getTitle = () => {
    const titles: { [key: string]: string } = {
      "/dashboard/settings": "Settings",
      "/dashboard/settings/profile": "Profile",
      "/dashboard/settings/profile/change-password": "Manage Password",
      "/dashboard/services": "Services",
      "/dashboard/developers": "Developers",
      "/admin/partners": "Manage Partners",
      "/admin/withdrawals": "Manage Withdrawals",
      "/admin/customers": "Manage Customers",
      "/admin/users": "Manage users",
      "/admin/manage-document": "Manage Customers Documents",
      "/admin/manage-monetary-zone": "Manage Monetary Zones",
      "/admin/countries-monetary-zone": "Manage Countries",
      "/admin/manage-service": "Manage Services",
      "/admin/manage-ip-address": "Manage IP Address",
      "/admin/maintenance": "Maintenance",
      "/admin/route-config": "Manage Config",
      "/dashboard/history": "Transactions History",
      "/admin/transaction-history": "Transactions History",
      "/admin/manage-roles": "Manage Roles",
      "/dashboard/payment-link": "Manage Payment Link",
      "/admin/payment-link": "Payment Links List",
      "/admin/manage-withdrawals": "Manage withdrawals",
      "/admin/payout": "Payout Management",
      "/dashboard/withdrawal-list": "withdrawal history",
      "/dashboard/payout/fundings-history": "Fundings History",
    };
    return titles[location.pathname] || "Dashboard";
  };

  const openConfirmModal = () => {
    setShowConfirmModal(true);
  };

  const handleConfirmRequestProduction = async () => {
    setRequestingProduction(true);
    setShowConfirmModal(false);
    try {
      const res = await customerService.requestToProduction();
      if (res.status === -1) {
        toastRef.current?.show({
          severity: "error",
          summary: "Error",
          detail: res.message || "Failed to request production mode",
          life: 3000,
        });
        return;
      }

      toastRef.current?.show({
        severity: "success",
        summary: "Success",
        detail: "Production request sent successfully",
        life: 3000,
      });

      // Refresh current user to update isRequestedToProduction status
      dispatch(getCurrentUser());
    } catch (error: any) {
      toastRef.current?.show({
        severity: "error",
        summary: "Error",
        detail: error.message || "Failed to request production mode",
        life: 3000,
      });
    } finally {
      setRequestingProduction(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout())
      .unwrap()
      .then(() => {
        navigate("/auth");
        localStorage.removeItem("otpConfirmed");
      })
      .catch((error) => {
        logMessage("Logout failed:", error);
      });
  };

  const handleProfileAction = (action: () => void) => {
    setIsProfileMenuOpen(false);
    action();
  };

  const profileItems = [
    { label: "Profile Details", icon: "pi pi-user", command: () => handleProfileAction(() => navigate("/dashboard/settings")) },
    { label: "Logout", icon: "pi pi-sign-out", command: () => handleProfileAction(handleLogout) },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (!profileMenuRef.current) return;
      if (!profileMenuRef.current.contains(event.target as Node)) {
        setIsProfileMenuOpen(false);
      }
    };

    if (isProfileMenuOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [isProfileMenuOpen]);

   useEffect(() => {
     if (currentUser?.role.role_code === "CUSTOMER"){
       dispatch(fetchAllBalances());
     }
   }, [dispatch]);

  const isCustomer = currentUser?.role.role_code === "CUSTOMER" || currentUser?.role.role_code === "ENTERPRISE";
  const isInProduction = customerMode === "PRODUCTION";
  const hasRequestedProduction = currentUser?.customer?.isRequestedToProduction;

  // Domain-based behavior
  const currentOrigin = window.location.origin;
  const isProdDomain = currentOrigin === process.env.REACT_APP_PROD_DOMAIN;
  const isSandboxDomain = currentOrigin === process.env.REACT_APP_SANDBOX_DOMAIN;
  const canShowSandboxSwitch = isSandboxDomain && isCustomer;

   const switchModeButtonLabel = isSandboxDomain ? "Go To Live Sandbox" : "Go to Production";
   const switchModeButtonClass = isSandboxDomain
     ? "bg-orange-500 hover:bg-orange-600 text-white"
     : "bg-green-500 hover:bg-green-600 text-white";

   const modeLabel = customerMode === "PRODUCTION"
     ? (isSandboxDomain ? "Live Sandbox" : "Production")
     : "Test";


  const modalHeader = (
    <div className="w-full flex items-center justify-between text-orange-700">
      <span className="font-semibold">{isSandboxDomain ? "Switch to Live Sandbox" : "Request Production Mode"}</span>
      <button
        type="button"
        onClick={() => setShowConfirmModal(false)}
        disabled={requestingProduction}
        className="text-orange-600 hover:text-orange-700 disabled:opacity-50"
        aria-label="Close"
      >
        <i className="pi pi-times" />
      </button>
    </div>
  );

  const modalMainText = isSandboxDomain
    ? <>Are you sure you want to switch to <strong>Live sandbox</strong> to use <strong>Payout</strong> transactions?</>
    : <>Are you sure you want to request <strong>Production Mode</strong>?</>;
  const modalStep3Text = isSandboxDomain
    ? "Once enabled, you can use Payout transactions in Live sandbox"
    : "Once approved, you can perform real transactions";
  const modalFooterText = isSandboxDomain
    ? "Please ensure your account setup is complete before enabling Live sandbox access."
    : "Please ensure all your KYC documents are verified before requesting production access.";

  const confirmModalFooter = (
    <div className="flex justify-end gap-2">
      <Button
        label="Cancel"
        icon="pi pi-times"
        onClick={() => setShowConfirmModal(false)}
        className="p-button-text"
        disabled={requestingProduction}
      />
      <Button
        label="Confirm Request"
        icon="pi pi-check"
        onClick={handleConfirmRequestProduction}
        loading={requestingProduction}
        className="bg-orange-500 hover:bg-orange-600 border-orange-500 text-white"
        autoFocus
      />
    </div>
  );

  return (
    <>
      {isSandboxDomain && showSandboxBanner && isCustomer && (
        <div className={`p-3 flex items-start justify-between bg-orange-50 border-orange-200`}>
          <div className="text-sm">
            {isInProduction ? (
              <p className="text-orange-700">
                You are currently using the application in <strong>Live Sandbox</strong> mode. Payout APIs are available.
              </p>
            ) : (
              <p className="text-orange-700">
                You are currently using the application in <strong>Sandbox</strong> mode. Switch to <strong>Live Sandbox</strong> mode to use PayOut APIs.
              </p>
            )}
          </div>
          <Button
            icon="pi pi-times"
            className="p-button-text p-button-sm"
            onClick={() => setShowSandboxBanner(false)}
            aria-label="Close sandbox alert"
          />
        </div>
      )}

      <header className="z-100 flex justify-between shadow-sm items-center bg-white p-7 py-4 h-20 border-b">

        <div className="flex items-center">
          <Button
            icon={isExpanded ? "pi pi-align-left" : "pi pi-align-right"}
            onClick={() => setIsExpanded(!isExpanded)}
            className="hidden md:block text-black p-0 border-none focus:outline-none focus:shadow-none"
          />

          <h1 className="hidden md:block text-md text-secondary-500">{getTitle()}</h1>

          {/* Affichage du mode actuel (lecture seule) */}
          {!loading && isCustomer && (
            <div className="p-4">
              <span className="text-secondary-500 text-sm font-medium">
                Mode: <span className={`font-bold ${customerMode === "PRODUCTION" ? "text-green-500" : "text-red-500"}`}>
                  {modeLabel}
                </span>
              </span>
            </div>
          )}
        </div>

        <div className="flex items-center space-x-4">
          {/* Go to Production Button - Only for customers not in production */}
          {!loading && canShowSandboxSwitch && !isInProduction && (
            <Button
              label={hasRequestedProduction ? "Request Pending" : switchModeButtonLabel}
              icon={hasRequestedProduction ? "pi pi-clock" : "pi pi-arrow-right"}
              className={`text-sm ${
                hasRequestedProduction 
                  ? "bg-orange-300 text-white cursor-not-allowed" 
                  : switchModeButtonClass
              }`}
              disabled={hasRequestedProduction || requestingProduction}
              loading={requestingProduction}
              onClick={openConfirmModal}
            />
          )}

          {!loading && isCustomer && (
            <div className="hidden md:flex items-center gap-3">
              {balances.length > 0 ? (
                <>
                  <div className="flex items-center gap-2 bg-gray-50 rounded-lg px-4 py-2 border border-gray-200">
                    <div className="flex flex-col gap-1">
                      {balances.map((balance) => (
                        <div key={balance.currency} className="flex items-center gap-3 text-sm">
                          <span className="font-semibold text-gray-700 min-w-[45px]">
                            {balance.currency}
                          </span>
                          <div className="flex items-center gap-2">
                            <span className="text-gray-600">
                              {showBalance ? (
                                <>
                                  Disponible:{" "}
                                  <strong className="text-primary-500">
                                    {formatAmount(balance.availableBalance)}
                                  </strong>
                                </>
                              ) : (
                                "******"
                              )}
                            </span>
                            {showBalance && (
                              <>
                                <span className="text-gray-400">|</span>
                                <span className="text-gray-600">
                                  Collecté:{" "}
                                  <strong className="text-green-600">
                                    {formatAmount(balance.totalCollected)}
                                  </strong>
                                </span>
                              </>
                            )}
                          </div>
                        </div>
                      ))}
                    </div>

                    <div className="flex flex-col gap-1 ml-2 border-l border-gray-300 pl-2">
                      <span
                        className="cursor-pointer text-primary-500 hover:text-primary-600 transition-colors"
                        onClick={() => setShowBalance(!showBalance)}
                        title={showBalance ? "Masquer" : "Afficher"}
                      >
                        <i className={showBalance ? "pi pi-eye-slash" : "pi pi-eye"}></i>
                      </span>
                      <span
                        className="cursor-pointer text-gray-400 hover:text-primary-500 transition-colors"
                        onClick={() => dispatch(fetchAllBalances())}
                        title="Rafraîchir"
                      >
                        <i className={`pi pi-refresh text-sm${isLoading ? " animate-spin" : ""}`}></i>
                      </span>
                    </div>
                  </div>
                </>
              ) : (
                <Skeleton style={{ width: "300px", height: "3rem" }} className="rounded-lg" />
              )}
            </div>
          )}
          <div className="hidden md:block">
           <NotificationButton/>
          </div>

          <div className="relative items-center" ref={profileMenuRef}>
            <Button
              label={`${currentUser?.firstname} ${currentUser?.lastname}`}
              icon="pi pi-angle-down"
              className="p-button-text text-secondary-500 flex items-center"
              onClick={(e) => {
                e.stopPropagation();
                setIsProfileMenuOpen((prev) => !prev);
              }}
            >
              <Avatar label={currentUser?.firstname?.[0]?.toUpperCase() || "V"} size="normal" className="text-secondary-500 w-8 h-8 ml-2" shape="circle" />
            </Button>

            {isProfileMenuOpen && (
              <div className="absolute right-0 mt-2 z-50">
                <PrimeMenu model={profileItems} />
              </div>
            )}
          </div>
        </div>

        {/* Confirmation Modal for Go to Production */}
        <Dialog
          visible={showConfirmModal && canShowSandboxSwitch}
          onHide={() => setShowConfirmModal(false)}
          header={modalHeader}
          footer={confirmModalFooter}
          style={{ width: "450px" }}
          closable={false}
          draggable={false}
          className="warning-modal"
          contentClassName="bg-orange-50"
          breakpoints={{ "960px": "80vw", "640px": "95vw" }}
        >
          <div className="py-4 sticky top-0">
            <div className="flex items-start gap-3">
              <i className="pi pi-exclamation-triangle text-orange-500" style={{ fontSize: "2rem" }} />
              <div>
                <p className="mb-3 text-orange-700">
                  {modalMainText}
                </p>
                <div className="bg-orange-50 border border-orange-300 rounded-lg p-3">
                  <p className="text-sm text-orange-700 mb-2">
                    <i className="pi pi-info-circle mr-2"></i>
                    <strong>What happens next:</strong>
                  </p>
                  <ul className="text-sm text-orange-700 list-disc list-inside space-y-1">
                    <li>Your request will be reviewed by our admin team</li>
                    <li>You will be notified once your request is processed</li>
                    <li>{modalStep3Text}</li>
                  </ul>
                </div>
                <p className="mt-3 text-sm text-orange-700">
                  {modalFooterText}
                </p>
              </div>
            </div>
          </div>
        </Dialog>

        <Toast ref={toastRef} />
      </header>
    </>
  );
};

export default NavBar;

